// JavaScript
// src/breweries/Breweries.jsx
// skipcq: JS-W1028, JS-0128
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
// utils
import { auth } from '../../utils/firebase';
import { UserContext } from '../../context/UserContext';
// material
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
// Dialog is moved to its own component
// Components
import DashboardLayout from '../../components/layout/DashboardLayout';
import BreweryCard from './BreweryCard';
import ButtonHeader from '../../components/widgets/ButtonHeader';
import SearchTextField from '../../components/common/SearchTextField';
import AddBreweryDialog from './AddBreweryDialog';
import CreateBreweryGroupDialog from './CreateBreweryGroupDialog';
// hooks
import { useBreweriesSearch } from '../../hooks/useBreweriesSearch';
import useDebouncedValue from '../../hooks/useDebouncedValue';
// data
import states from '../../shared/states/usStates.json';
const LOCAL_STORAGE_KEY = 'breweries.usState';

function StateSelector({ usState, handleChange }) {
  const stateMenuItems = useMemo(
    () =>
      states.map((state) => (
        <MenuItem value={state.code} key={state.code}>
          {state.name}
        </MenuItem>
      )),
    [],
  );
  return (
    <FormControl sx={{ width: 300, mx: 2.5, my: 0.5 }}>
      <InputLabel>State</InputLabel>
      <Select
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={usState}
        label="State"
        onChange={handleChange}
      >
        {stateMenuItems}
      </Select>
    </FormControl>
  );
}

function Breweries() {
  const [selectedForMergeIds, setSelectedForMergeIds] = useState([]);
  const [usState, setUsState] = useState(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY) || 'NH';
    } catch (_) {
      return 'NH';
    }
  });

  const pageTitle = 'Breweries';
  const navigate = useNavigate();
  const location = useLocation();
  const user = useContext(UserContext);
  const isGlobalAdmin = user?.isGlobalAdmin;
  const [authUser] = useAuthState(auth);

  // Add Brewery dialog state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [createGroupPrefillMembers, setCreateGroupPrefillMembers] = useState([]);
  // Search state
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [claimedOnly, setClaimedOnly] = useState(false);

  // Get deleted brewery ID from location state (passed after deletion)
  const deletedBreweryIdFromLocation = location.state?.deletedBreweryId;

  // Use custom hook for brewery search and deleted tracking
  const {
    breweries,
    isLoading,
    error: breweriesSearchError,
    deletedBreweryIds,
  } = useBreweriesSearch(usState, debouncedSearch, deletedBreweryIdFromLocation, claimedOnly);

  // Persist selected state across navigations
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, usState);
    } catch (_) {
      // ignore write errors (e.g., private mode)
    }
  }, [usState]);

  // Clear location state after capturing the deleted brewery ID
  useEffect(() => {
    if (deletedBreweryIdFromLocation) {
      // The hook handles adding the ID to tracking, we just need to clear location state
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [deletedBreweryIdFromLocation, location, navigate]);

  // Restore scroll position when returning from a brewery detail page
  useEffect(() => {
    if (isLoading) {
      return;
    }
    try {
      const anchorId = sessionStorage.getItem('breweries.anchorId');

      if (anchorId) {
        const element = document.getElementById(`brewery-card-${anchorId}`);
        if (element) {
          element.scrollIntoView({ block: 'start' });
          return;
        }
      }
    } catch (_) {
      // ignore sessionStorage errors
    }
  }, [isLoading, breweries.length, usState]);

  const handleChange = useCallback((event) => {
    setUsState(event.target.value);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  const handleClaimedOnlyChange = useCallback((event) => {
    setClaimedOnly(event.target.checked);
  }, []);

  // Track selection changes from BreweryCard
  const handleCardSelectForMergeChange = useCallback((breweryId, checked) => {
    setSelectedForMergeIds((prev) => {
      if (checked) {
        return prev.includes(breweryId) ? prev : [...prev, breweryId];
      }
      return prev.filter((id) => id !== breweryId);
    });
  }, []);

  // Trigger merge navigation when exactly two breweries are selected
  const handleMergeClick = useCallback(() => {
    if (selectedForMergeIds.length !== 2) {
      return;
    }
    const [idA, idB] = selectedForMergeIds;
    navigate(`/breweries/merge/${idA}/${idB}`);
  }, [selectedForMergeIds, navigate]);

  // Handlers for Add Brewery dialog
  const handleOpenAddDialog = useCallback(() => {
    setIsAddOpen(true);
  }, []);

  const handleCloseAddDialog = useCallback(() => {
    setIsAddOpen(false);
  }, []);

  const handleCreated = useCallback(
    (breweryId) => {
      if (breweryId) {
        navigate(`/brewery/${breweryId}`);
      }
    },
    [navigate],
  );

  const renderBreweryCard = useCallback(
    (brewery) => (
      <BreweryCard
        key={brewery.id}
        id={brewery.id}
        brewery={brewery.data}
        selectedForMerge={selectedForMergeIds.includes(brewery.id)}
        onSelectForMergeChange={handleCardSelectForMergeChange}
      />
    ),
    [selectedForMergeIds, handleCardSelectForMergeChange],
  );

  // Filter out deleted breweries (persists across Typesense refreshes)
  const filteredBreweries = useMemo(() => {
    if (deletedBreweryIds.length > 0) {
      return breweries.filter(
        (brewery) => !deletedBreweryIds.includes(brewery.id),
      );
    }
    return breweries;
  }, [breweries, deletedBreweryIds]);

  const handleOpenCreateGroupClick = useCallback(() => {
    if (selectedForMergeIds.length < 2) {
      return;
    }
    const prefillRowsBuilt = selectedForMergeIds
      .map((breweryIdParam) => {
        const matchRow = filteredBreweries.find(
          (breweryEntry) => breweryEntry.id === breweryIdParam,
        );
        return matchRow ? { id: matchRow.id, data: matchRow.data } : null;
      })
      .filter(Boolean);
    setCreateGroupPrefillMembers(prefillRowsBuilt);
    setIsCreateGroupOpen(true);
  }, [selectedForMergeIds, filteredBreweries]);

  const handleCloseCreateGroupDialog = useCallback(() => {
    setIsCreateGroupOpen(false);
    setSelectedForMergeIds([]);
  }, []);

  const handleCreateGroupCompleted = useCallback(() => {
    setSelectedForMergeIds([]);
  }, []);

  const renderedCards = useMemo(
    () => filteredBreweries.map(renderBreweryCard),
    [filteredBreweries, renderBreweryCard],
  );

  return (
    <DashboardLayout title={pageTitle}>
      <ButtonHeader sticky fullWidth dense>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-around',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <StateSelector usState={usState} handleChange={handleChange} />
            <SearchTextField
              value={searchText}
              onChange={handleSearchChange}
              label="Search"
              loading={isLoading}
              placeholder="Search breweries..."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={claimedOnly}
                  onChange={handleClaimedOnlyChange}
                  color="primary"
                />
              }
              label="Claim approved only"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-around',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={handleMergeClick}
              disabled={selectedForMergeIds.length !== 2}
            >
              Merge
            </Button>
            <Button
              variant="contained"
              onClick={handleOpenCreateGroupClick}
              disabled={selectedForMergeIds.length < 2}
            >
              Create Group
            </Button>
            {isGlobalAdmin ? (
              <Button variant="contained" onClick={handleOpenAddDialog}>
                Add Brewery
              </Button>
            ) : null}
          </Box>
        </Box>
      </ButtonHeader>
      <Container fixed>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh"
            }}>
            <CircularProgress />
          </Box>
        ) : breweriesSearchError ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="error">
              {breweriesSearchError}
            </Typography>
          </Box>
        ) : (
          <Grid
            container
            spacing={0}
            direction="column"
            style={{ minHeight: '100vh' }}
            sx={{
              alignItems: "center"
            }}
          >
            {renderedCards}
          </Grid>
        )}
      </Container>
      {/* Add Brewery Dialog */}
      <AddBreweryDialog
        open={isAddOpen}
        onClose={handleCloseAddDialog}
        usState={usState}
        isGlobalAdmin={isGlobalAdmin}
        authUser={authUser}
        onCreated={handleCreated}
      />
      <CreateBreweryGroupDialog
        open={isCreateGroupOpen}
        onClose={handleCloseCreateGroupDialog}
        prefillMembers={createGroupPrefillMembers}
        onCreated={handleCreateGroupCompleted}
      />
    </DashboardLayout>
  );
}

export default Breweries;
